import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useGroupChatContext } from '../../context/groupchatContext';
import { usePusherContext } from '../../context/pusherContext';
import { debounce } from 'lodash';
import MessageList from './messageList';
import InfiniteScroll from 'react-infinite-scroll-component';
import { FaSpinner } from 'react-icons/fa';
import { useGetLocationChatMessagesQuery, useGetPatientChatsQuery } from '../../services/chatService';
import msgSound from '../../assets/audio/pop-alert.mp3';
import useSound from 'use-sound';
import { isLoggedInUser, isPatientChatMessage } from '../../helpers/utility';

const ChatWindow = React.memo(() => {

    const [pagination, setPagination] = useState({limit: 50, before_id: '', after_id: ''});
    const scrollRef = useRef();
    const groupChatContext = useGroupChatContext();
    const pusherContext = usePusherContext();
    const [playSound] = useSound(msgSound);
    const [scrollMessageId, setScrollMessageId] = useState(0);

    const {authData, selectedChat, messages, setMessages, pusherData, searchedMessagethread, setSearchedMessageThread, isUrgentMessage, getPendingMessages, removePendingMessage} = useMemo(() => ({
        authData: groupChatContext.authData,
        selectedChat: groupChatContext.selectedChat,
        messages: groupChatContext.messages,
        setMessages: groupChatContext.setMessages,
        pusherData: pusherContext.pusherData,
        searchedMessagethread: groupChatContext.searchedMessagethread,
        setSearchedMessageThread: groupChatContext.setSearchedMessageThread,
        isUrgentMessage: groupChatContext.isUrgentMessage,
        getPendingMessages: groupChatContext.getPendingMessages,
        removePendingMessage: groupChatContext.removePendingMessage,
    }));

    const {data: patientMsgData, isFetching} = useGetPatientChatsQuery({pid: selectedChat?.pid, ...pagination,urgent: isUrgentMessage}, { skip: !selectedChat?.pid, refetchOnMountOrArgChange: true });
    const {data: locationMsgData, isFetching: isLocationMsgFetching} = useGetLocationChatMessagesQuery({location_id: selectedChat?.location_id, ...pagination, urgent: isUrgentMessage}, { skip: (selectedChat?.pid && !selectedChat?.location_id), refetchOnMountOrArgChange: true });
    
    useEffect(()=>{
        if(patientMsgData || locationMsgData){
            const msgData = isPatientChatMessage(selectedChat) ? patientMsgData : locationMsgData;
            let msgs = msgData?.data || [];
            if(pagination.before_id){
                msgs = [...messages.data, ...msgs];
            }
            else if(pagination.after_id){
                msgs = [...msgs, ...messages.data];
            }
            
            const pendingMsgs = getPendingMessages(selectedChat);
            
            if(pendingMsgs && pendingMsgs.length > 0){
                const fetchedIds = new Set(msgs.map(m => m.id).filter(Boolean));
                
                pendingMsgs.forEach(pendingMsg => {
                    const wasFound = msgs.some(m => 
                        m.id && 
                        m.message?.text === pendingMsg.message?.text && 
                        Math.abs(new Date(m.created) - Date.now()) < 60000
                    );
                    
                    if(wasFound){
                        removePendingMessage(selectedChat, pendingMsg.temp_message_id);
                    }
                });
                
                const updatedPendingMsgs = getPendingMessages(selectedChat);
                
                if(updatedPendingMsgs.length > 0){
                    const existingTempIds = new Set(msgs.map(m => m.temp_message_id).filter(Boolean));
                    const uniquePendingMsgs = updatedPendingMsgs.filter(pm => !existingTempIds.has(pm.temp_message_id));
                    msgs = [...uniquePendingMsgs, ...msgs];
                }
            }
            
            setMessages({...messages, data: msgs, meta: msgData?.meta});
            if(scrollMessageId > 0){
                setTimeout(()=>{
                    scrollToSpecificMessage(scrollMessageId);
                    setScrollMessageId(0)
                }, 10)
            }
        }
    }, [patientMsgData, locationMsgData])

    useEffect(()=>{
        if(searchedMessagethread?.thread_id){
            if(searchedMessagethread.data){
                setMessages({...messages, data: searchedMessagethread.data});
            }
            setTimeout(()=>{
                scrollToSpecificMessage(searchedMessagethread?.thread_id);
            }, 10)
        }
    }, [searchedMessagethread])

    useEffect(() => {
        setPagination({...pagination, before_id: '', after_id: ''})
        scrollToBottom();
        setSearchedMessageThread({thread_id: '', data:[]})
    }, [selectedChat])

    const scrollToSpecificMessage = (id) => {
        if(id){
            const element = document.getElementById(id);
            if(element){
                scrollRef.current.scrollTo({
                    top: element.offsetTop - 90, 
                    behavior: 'smooth'
                });
            }
        }
    }

    const scrollToBottom = () => {
        if(scrollRef.current){
            scrollRef.current.scrollTo(0, scrollRef.current.scrollHeight);
        }
    }

    useEffect(()=>{

        if(!pusherData || !selectedChat){
            return;
        }

        const isCurrentChatOpened = isPatientChatMessage(selectedChat) ? pusherData.pid == selectedChat.pid : (selectedChat.location_id == pusherData.location?.id && !pusherData.pid);
        if(isCurrentChatOpened &&
            (!isLoggedInUser(authData, pusherData) || (isLoggedInUser(authData, pusherData) && (pusherData.message.report_problem || pusherData.message.type == 'event')))){
            
            setMessages({...messages, data: [pusherData, ...messages.data]})
            scrollToBottom();
            playSound();
        }
    }, [pusherData])

    const handleScroll = useMemo(()=> debounce(() => {

        const container = scrollRef.current;

        // Get all spans with the same ID within the container
        // const spans = container.querySelectorAll('#msg_day');

        // Get the container's scroll position. Since the container is reversed (column-reverse), 
        // scrollTop represents the distance from the bottom of the content to the bottom of the container.
        const containerTop = container.scrollTop;
        
        if(containerTop >= 0 && containerTop <= 0.5){
            setScrollMessageId(messages.data[0]?.id);
            setPagination({...pagination, before_id: '', after_id: messages.data[0]?.id})
        }

    }, 500), [messages])

    const fetchMoreMessages = () => {
        const msgsLength = messages.data.length;
        setPagination({...pagination, after_id: '', before_id: messages.data[msgsLength -1]?.id})
    }

    return (
        <>
            {(isFetching || isLocationMsgFetching) && (
                <div className="justify-center items-center flex overflow-x-hidden overflow-y-auto absolute inset-0 outline-none focus:outline-none before:absolute before:flex before:w-full before:h-full z-[999]">
                    <FaSpinner className="animate-spin text-green-300-500 ml-1" size={40} />
                </div>
            )}

            {/* {isMsgDateDay ?
                <div className='py-2 flex absolute top-0 w-full z-10 justify-center items-center'>
                    <span className='px-2 py-1 shadow font-medium rounded-lg text-gray-400 bg-white border-gray-300'>
                        {msgDateDay}
                    </span>
                </div>
            : null} */}

            <div className='flex-1 overflow-hidden'>
                <div id="scrollableDiv" ref={scrollRef} className="flex relative p-3 border-r overflow-y-auto flex-col-reverse h-full">
                    {/*Put the scroll bar always on the bottom*/}
                    <InfiniteScroll
                        dataLength={messages.data.length}
                        next={fetchMoreMessages}
                        style={{ display: 'flex', flexDirection: 'column-reverse' }} //To put endMessage and loader to the top.
                        inverse={true}
                        onScroll={handleScroll}
                        hasMore={messages.meta.count > messages.data.length}
                        // loader={<h4 className='mt-6'>Loading...</h4>}
                        // endMessage={
                        //     <p className='text-center text-gray-400 font-small mt-7'>
                        //       <b>You have seen it all</b>
                        //     </p>
                        // }
                        scrollableTarget="scrollableDiv"
                    >
                        <MessageList messages={messages.data} />
                    </InfiniteScroll>
                </div>
            </div>
        </>
    );
});

export default ChatWindow;